import { useT } from "@transifex/react";
import Checkbox from "../../../basic/checkbox/Checkbox";
import { FEDERAL_STATES, NO_FEDERAL_STATE, type FederalStateChoice } from "../../../constants/FederalStates";
import BookSelectionStepHeading from "./BookSelectionStepHeading";
import FederalStateFlag from "./FederalStateFlag";
import SelectionCard from "./SelectionCard";

const FLAG_CLASSES = "aspect-3/2 w-xl shrink-0 rounded-xsmall-container ring-1 ring-transparent-black";

interface FederalStateStepProps {
    federalState: FederalStateChoice | null;
    shouldRemember: boolean;
    onRememberChange: (shouldRemember: boolean) => void;
    onPick: (choice: FederalStateChoice) => void;
}

const FederalStateStep = (props: FederalStateStepProps) => {
    const t = useT();
    const t_title = t("In which federal state do you teach?", { _tags: "CreateTest,SelectBook" });
    const t_hint = t("Some textbooks are only approved in certain federal states.", {
        _tags: "CreateTest,SelectBook",
    });
    const t_remember = t("Remember federal state", { _tags: "CreateTest,SelectBook" });
    const t_noFederalState = t("No federal state", { _tags: "CreateTest,SelectBook" });

    const choices: Array<{ choice: FederalStateChoice; name: string }> = [
        ...FEDERAL_STATES.map((state) => ({ choice: state.code, name: state.name })),
        { choice: NO_FEDERAL_STATE, name: t_noFederalState },
    ];

    return (
        <div className="flex flex-col gap-s">
            {/* The checkbox goes below the heading once the two don't fit side by side. */}
            <div className="flex flex-wrap items-end justify-between gap-s">
                <BookSelectionStepHeading
                    title={t_title}
                    hint={t_hint}
                />
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={props.shouldRemember}
                    onClick={() => props.onRememberChange(!props.shouldRemember)}
                    className="flex cursor-pointer items-center gap-s rounded-small-container border-2 border-solid border-p6-grey-lightest bg-background-light px-s py-xs hover:border-creation-primary"
                    data-testid="book-selection-remember-state"
                >
                    <Checkbox
                        isChecked={props.shouldRemember}
                        checkedClassName="border-creation-primary bg-creation-primary"
                    />
                    <span className="text-m font-medium text-p6-grey-dark">{t_remember}</span>
                </button>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(var(--size-selection-card),1fr))] gap-s">
                {choices.map(({ choice, name }) => (
                    <SelectionCard
                        key={choice}
                        isSelected={props.federalState === choice}
                        onClick={() => props.onPick(choice)}
                        data-testid={`book-selection-state-${choice}`}
                    >
                        <FederalStateFlag
                            choice={choice}
                            className={FLAG_CLASSES}
                        />
                        <span className="text-l font-medium text-p6-grey-dark">{name}</span>
                    </SelectionCard>
                ))}
            </div>
        </div>
    );
};

export default FederalStateStep;
